<template>
	<div class="gantt">
		<slot name="top-section">
			<v-row no-gutters justify="space-between" align="end">
				<v-col v-if="topSectionPosition == 'left'" cols="12" sm="8">
					<slot name="app-bar-btn" />
				</v-col>
				<v-col cols="12" sm="5">
					<v-row no-gutters align="baseline">
						<slot name="left-time-btn" />
						<v-btn
							v-if="!onlyDateChartType"
							color="secondary"
							@click="
								changeChartType(setDateForChartTypeChange, chartType == 'time' ? 'date' : 'time')
							"
							><v-icon class="mr-1">mdi-swap-horizontal</v-icon>
							<template v-if="chartType == 'time'"> 日毎を表示 </template>
							<template v-else-if="chartType == 'date'"> 時間毎を表示 </template>
						</v-btn>
						<slot name="right-time-btn" />
						<v-col cols="12" sm="6">
							<v-menu
								ref="menu"
								v-model="menu"
								:close-on-content-click="false"
								transition="scale-transition"
								offset-y
								min-width="auto"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-text-field
										v-model="displayDate"
										label="日付を検索"
										class="ml-2 mt-sm-0 mt-2"
										prepend-inner-con="mdi-calendar"
										readonly
										hide-details=""
										v-bind="attrs"
										v-on="on"
									></v-text-field>
								</template>
								<v-date-picker v-model="displayDate" no-title scrollable locale="ja-JP">
									<v-spacer></v-spacer>
									<v-btn text color="primary" @click="menu = false"> 閉じる </v-btn>
								</v-date-picker>
							</v-menu>
						</v-col>
					</v-row>
				</v-col>
				<v-col v-if="topSectionPosition == 'right'" cols="12" sm="7">
					<slot name="app-bar-btn" />
				</v-col>
			</v-row>

			<slot name="above-chart" />
		</slot>

		<div class="gantt-chart mt-4">
			<div v-if="!$vuetify.breakpoint.xs">
				<v-row
					no-gutters
					class="gantt-tasks"
					ref="task"
					:style="{ height: `${taskTitleHeight + topDateUnitHeight}px` }"
				>
					<slot name="task-top-section" :showDates="showDates" :taskTitle="taskTitle">
						<v-col :cols="showDates ? 6 : 12" class="gantt-task">{{ taskTitle }}</v-col>
						<template v-if="showDates">
							<v-col cols="3" class="gantt-start">開始</v-col>
							<v-col cols="3" class="gantt-end">完了</v-col>
						</template>
					</slot>
				</v-row>

				<div ref="taskSection" @drop="dragUpdateTask()">
					<div
						v-for="(task, index) in lists"
						:key="index"
						class="gantt-task-list"
						@dragstart="dragTask(task)"
						@dragover.prevent="dragTaskOver(task)"
					>
						<!-- @dragend="dragUpdateTask(task)" -->
						<template v-if="task.cat === 'category'">
							<slot
								name="category"
								:styleObject="styleObject"
								:task="task"
								:toggleCategory="toggleCategory"
							>
								<div
									draggable="false"
									:class="['categories', categoryClickable ? 'clickable' : 'no-click']"
									@click="categoryClickable ? clickCategory(task.id) : null"
								>
									<slot name="category-name" :styleObject="styleObject" :task="task">
										<span :style="styleObject">{{ task[categoryNameKey] }}</span>
									</slot>

									<div class="pr-4" @click.stop="toggleCategory(task.id)">
										<span v-if="task.collapsed">
											<v-icon>mdi-chevron-right</v-icon>
										</span>
										<span v-else>
											<v-icon>mdi-chevron-down</v-icon>
										</span>
									</div>
								</div>
							</slot>
						</template>

						<v-row
							v-else
							no-gutters
							:class="[
								'tasks',
								taskClickable ? 'clickable' : 'no-click',
								showTray ? 'grabbable' : '',
							]"
							:draggable="isDraggable"
							@click="taskClickable ? clickTask(task.id) : null"
						>
							<slot
								name="task-list"
								:task="task"
								:taskNameKey="taskNameKey"
								:startDateKey="startDateKey"
								:endDateKey="endDateKey"
								:startTimeKey="startTimeKey"
								:endTimeKey="endTimeKey"
								:showDates="showDates"
							>
								<v-col :cols="showDates ? 6 : 12" class="task-name">
									<slot name="task-list-name" :task="task">
										{{ task[taskNameKey] }}
									</slot>
								</v-col>
								<template v-if="showDates">
									<v-col cols="3" class="task-start" @click.stop="clickStartTime(task)">
										<slot
											name="task-start"
											:task="task"
											:startDateKey="startDateKey"
											:startTimeKey="startTimeKey"
										>
											{{ task[startDateKey] | dateFormatter }}
										</slot>
									</v-col>
									<v-col cols="3" class="task-end" @click.stop="clickEndTime(task)">
										<slot
											name="task-end"
											:task="task"
											:endDateKey="endDateKey"
											:endTimeKey="endTimeKey"
										>
											{{ task[endDateKey] | dateFormatter }}
										</slot>
									</v-col>
								</template>
							</slot>
						</v-row>
					</div>
					<div v-if="!lists.length" class="gantt-task-list text-center pa-1">
						{{noTaskText}}
					</div>
				</div>
			</div>

			<OneDayGanttChart
				v-if="chartType == 'one-day'"
				ref="ganttchart"
				:isDraggable="isDraggable"
				:changeDatetimeFlag="changeDatetimeFlag"
				:categories="categories"
				:tasks="tasks"
				:block_size="time_block_size"
				:notDistributedTasks="notDistributedTasks"
				:categoryNameKey="categoryNameKey"
				:taskNameKey="taskNameKey"
				:startDateKey="startDateKey"
				:endDateKey="endDateKey"
				:startTimeKey="startTimeKey"
				:endTimeKey="endTimeKey"
				:fontSize="fontSize"
				:isWindowSizeCheck="isWindowSizeCheck"
				:changeCategoryTaskFlag="changeCategoryTaskFlag"
				:showDates="showDates"
				:taskArrayName="taskArrayName"
				:taskBarNameStyle="taskBarNameStyle"
				:taskBarStyle="taskBarStyle"
				:colorRecognitionKey="colorRecognitionKey"
				:taskBarColorsByRecognitionKey="taskBarColorsByRecognitionKey"
				:editableRecognitionKey="editableRecognitionKey"
				:editableByRecognitionKey="editableByRecognitionKey"
				:taskBarType="taskBarType"
				:task_width="task_width"
				:task_height="task_height"
				:start_month="start_month"
				:end_month="end_month"
				:lists="lists"
				:onlyDateChartType="onlyDateChartType"
				:topDateUnitHeight="topDateUnitHeight"
				:showCells="showCells"
				:darkColorUntil="darkColorUntil"
				:darkColorFrom="darkColorFrom"
				:barClickable="barClickable"
				@click-bar="$emit('click-bar', $event)"
				@change-datetime="$emit('change-datetime', $event)"
				@change-period="changePeriod($event)"
				@change-chart-type="changeChartType($event, 'time')"
			>
				<template v-slot:top-date-unit="{ date }">
					<slot name="top-date-unit" :date="date" />
				</template>
				<template v-slot:task-bar="{ task, mouseDownMove, mouseDownResize }">
					<slot
						name="task-bar"
						:task="task"
						:mouseDownMove="mouseDownMove"
						:mouseDownResize="mouseDownResize"
				/></template>
				<template v-slot:task-name="{ task }">
					<slot name="task-name" :task="task" />
				</template>
				<template v-slot:hover-card="{ hoverPosition, task, hover, taskLine }">
					<slot
						name="hover-card"
						:hover="hover"
						:hoverPosition="hoverPosition"
						:task="task"
						:taskLine="taskLine"
					/>
				</template>
			</OneDayGanttChart>
			<DateGanttChart
				v-else-if="chartType == 'date'"
				ref="ganttchart"
				:isDraggable="isDraggable"
				:changeDatetimeFlag="changeDatetimeFlag"
				:categories="categories"
				:tasks="tasks"
				:block_size="block_size"
				:notDistributedTasks="notDistributedTasks"
				:categoryNameKey="categoryNameKey"
				:taskNameKey="taskNameKey"
				:startDateKey="startDateKey"
				:endDateKey="endDateKey"
				:startTimeKey="startTimeKey"
				:endTimeKey="endTimeKey"
				:fontSize="fontSize"
				:isWindowSizeCheck="isWindowSizeCheck"
				:changeCategoryTaskFlag="changeCategoryTaskFlag"
				:showDates="showDates"
				:taskArrayName="taskArrayName"
				:taskBarNameStyle="taskBarNameStyle"
				:taskBarStyle="taskBarStyle"
				:colorRecognitionKey="colorRecognitionKey"
				:taskBarColorsByRecognitionKey="taskBarColorsByRecognitionKey"
				:editableRecognitionKey="editableRecognitionKey"
				:editableByRecognitionKey="editableByRecognitionKey"
				:taskBarType="taskBarType"
				:task_width="task_width"
				:task_height="task_height"
				:start_month="start_month"
				:end_month="end_month"
				:lists="lists"
				:onlyDateChartType="onlyDateChartType"
				:topDateUnitHeight="topDateUnitHeight"
				:showCells="showCells"
				:darkColorUntil="darkColorUntil"
				:darkColorFrom="darkColorFrom"
				:barClickable="barClickable"
				@click-bar="$emit('click-bar', $event)"
				@change-datetime="$emit('change-datetime', $event)"
				@change-period="changePeriod($event)"
				@change-chart-type="changeChartType($event, 'time')"
			>
				<template v-slot:top-date-unit="{ date }">
					<slot name="top-date-unit" :date="date" />
				</template>
				<template v-slot:task-bar="{ task, mouseDownMove, mouseDownResize }">
					<slot
						name="task-bar"
						:task="task"
						:mouseDownMove="mouseDownMove"
						:mouseDownResize="mouseDownResize"
				/></template>
				<template v-slot:task-name="{ task }">
					<slot name="task-name" :task="task" />
				</template>
				<template v-slot:hover-card="{ hoverPosition, task, hover, taskLine }">
					<slot
						name="hover-card"
						:hover="hover"
						:hoverPosition="hoverPosition"
						:task="task"
						:taskLine="taskLine"
					/>
				</template>
			</DateGanttChart>
			<TimeGanttChart
				v-else-if="chartType == 'time'"
				ref="ganttchart"
				:isDraggable="isDraggable"
				:changeDatetimeFlag="changeDatetimeFlag"
				:tasks="tasks"
				:block_size="time_block_size"
				:notDistributedTasks="notDistributedTasks"
				:categoryNameKey="categoryNameKey"
				:taskNameKey="taskNameKey"
				:startDateKey="startDateKey"
				:endDateKey="endDateKey"
				:startTimeKey="startTimeKey"
				:endTimeKey="endTimeKey"
				:fontSize="fontSize"
				:isWindowSizeCheck="isWindowSizeCheck"
				:changeCategoryTaskFlag="changeCategoryTaskFlag"
				:showDates="showDates"
				:taskArrayName="taskArrayName"
				:taskBarNameStyle="taskBarNameStyle"
				:taskBarStyle="taskBarStyle"
				:colorRecognitionKey="colorRecognitionKey"
				:taskBarColorsByRecognitionKey="taskBarColorsByRecognitionKey"
				:editableRecognitionKey="editableRecognitionKey"
				:editableByRecognitionKey="editableByRecognitionKey"
				:taskBarType="taskBarType"
				:task_width="task_width"
				:task_height="task_height"
				:start_month="start_month"
				:end_month="end_month"
				:lists="lists"
				:barClickable="barClickable"
				@click-bar="$emit('click-bar', $event)"
				@change-datetime="$emit('change-datetime', $event)"
				@change-period="changePeriod($event)"
				@change-chart-type="changeChartType($event, 'date')"
			>
				<template v-slot:task-bar="{ task, mouseDownMove, mouseDownResize }">
					<slot
						name="task-bar"
						:task="task"
						:mouseDownMove="mouseDownMove"
						:mouseDownResize="mouseDownResize"
				/></template>
				<template v-slot:task-name="{ task }">
					<slot name="task-name" :task="task" />
				</template>
				<template v-slot:hover-card="{ hoverPosition, task, hover, taskLine }">
					<slot
						name="hover-card"
						:hover="hover"
						:hoverPosition="hoverPosition"
						:task="task"
						:taskLine="taskLine"
					/>
				</template>
			</TimeGanttChart>
		</div>
		<slot name="below-chart" />

		<div class="position-relative" v-show="showTray">
			<div class="modified-height" />
			<v-card
				tile
				class="bottom-sheet"
				@dragover.prevent
				@drop.prevent="dragUpdateTaskInTray($event)"
				@dragend="dragUpdateTask()"
			>
				<v-btn dark fab small color="red" fixed class="fab-btn" @click="showTray = false"
					><v-icon dark>mdi-close</v-icon></v-btn
				>
				<v-row v-if="taskWithoutCategoryId.length" no-gutters class="pa-2">
					<v-card
						class="gantt-task-list mr-2 mb-2 grabbable"
						v-for="task in taskWithoutCategoryId"
						:key="task.id"
						draggable
						@dragstart="dragTask(task)"
						@dragover.prevent="dragTaskOver(task)"
						@dragend="dragUpdateTask(task)"
					>
						<slot
							name="task-list"
							:task="task"
							:taskNameKey="taskNameKey"
							:startDateKey="startDateKey"
							:endDateKey="endDateKey"
							:startTimeKey="startTimeKey"
							:endTimeKey="endTimeKey"
							:showDates="false"
						>
							{{ task[taskNameKey] }}
						</slot>
					</v-card>
				</v-row>
				<p v-else class="text-center">
					<slot name="no-categorized-task-text"> タスクがありません </slot>
				</p>
			</v-card>
		</div>
	</div>
</template>

<script>
import moment from "moment/min/moment.min.js"
import TimeGanttChart from "./ganttchart/TimeGanttChart.vue"
import DateGanttChart from "./ganttchart/DateGanttChart.vue"
import OneDayGanttChart from "./ganttchart/OneDayGanttChart.vue"

export default {
	filters: {
		dateFormatter: function (date) {
			if (!date) return ""
			return moment(date).format("YYYY/MM/DD")
		},
	},
	components: { TimeGanttChart, DateGanttChart, OneDayGanttChart },
	props: {
		taskTitle: {
			type: String,
			required: false,
			default: "タスク",
		},
		//draggableの制御
		isDraggable: {
			type: Boolean,
			default: false,
		},
		categoryClickable: {
			type: Boolean,
			default: false,
		},
		taskClickable: {
			type: Boolean,
			default: false,
		},

		// trueであればバーの拡大・縮小・移動が可能
		changeDatetimeFlag: {
			type: Boolean,
			default: false,
		},
		// カテゴリーのデータ
		categories: {
			type: Array,
			default: () => [],
		},
		// タスクのデータ
		tasks: {
			type: Array,
			default: () => [],
		},
		// カテゴリーと紐付けされていないタスクのデータ
		notDistributedTasks: {
			type: Array,
			default: () => [],
		},
		topSectionPosition: {
			type: String,
			required: false,
			default: "right",
		},
		categoryNameKey: {
			type: String,
			default: "",
		},
		taskNameKey: {
			type: String,
			default: "",
		},
		startDateKey: {
			type: String,
			default: "start_date",
		},
		endDateKey: {
			type: String,
			default: "end_date",
		},
		startTimeKey: {
			type: String,
			default: "",
		},
		endTimeKey: {
			type: String,
			default: "",
		},
		display_date: {
			type: String,
			default: "",
		},
		fontSize: {
			type: String,
			default: "16px",
		},
		categoryRecognitionId: {
			type: String,
			required: false,
			default: "category_id",
		},
		isWindowSizeCheck: {
			type: Boolean,
			default: false,
		},
		changeCategoryTaskFlag: {
			type: Boolean,
			default: false,
		},
		// タスクの開始・完了日を表示するか
		showDates: {
			type: Boolean,
			default: true,
		},
		styleObject: {
			type: Object,
			required: false,
			default: () => ({}),
		},
		// 一行に複数タスクバーを表示する場合の参照する行列のキー
		taskArrayName: {
			type: null,
			default: null,
			required: false,
		},
		// タスクバー内のpタグのcss
		taskBarNameStyle: {
			type: Object,
			required: false,
			default: () => ({}),
		},
		// タスクバー用のcss
		taskBarStyle: {
			type: Object,
			required: false,
			default: () => ({}),
		},
		// 背景色を分ける場合はそれを分けるためのキー
		colorRecognitionKey: {
			type: String,
			required: false,
		},
		// colorRecognitionKeyを指定する場合は，colorRecognitionKeyの値をキー、色をバリューの形でオブジェクトで受け取る
		// 例） {pending: "skyblue", ongoing:"orange", completed:"green", canceled:"grey"}
		taskBarColorsByRecognitionKey: {
			type: Object,
			required: false,
		},
		// 編集可能（ドラッグ）を分ける場合はそれを分けるためのキー
		editableRecognitionKey: {
			type: String,
			required: false,
		},
		// editableRecognitionKeyを指定する場合は，editableRecognitionKeyの値をキー、結果をバリューの形でオブジェクトで受け取る
		// また、editableRecognitionKeyを指定する場合は、changeDatetimeFlagをtrueにする必要がある
		// 例） {pending: true, ongoing:true, completed:false, canceled:false}
		// または {pending:{right: true, left: true, drag: true}, ongoing:{right: true, left: true, drag: true}, completed:{right: false, left: false, drag: false}, canceled:{right: false, left: false, drag: false}}
		editableByRecognitionKey: {
			type: Object,
			required: false,
		},
		taskBarType: {
			type: String,
			required: false,
			default: "bar",
		},
		// 開始日
		start_date: {
			type: String,
			required: false,
			default: "",
		},
		// 終了日
		end_date: {
			type: String,
			required: false,
			default: "",
		},
		// chartTypeに使用
		chart_type: {
			type: String,
			required: false,
			default: "date",
		},
		// chartTypeをsyncするか
		syncChartType: {
			type: Boolean,
			required: false,
			default: false,
		},
		noTaskText: {
			type: String,
			required: false,
			default: "適当データがありません。",
		},
		dateChartSpan: {
			type: Number,
			required: false,
			default: 1,
		},
		dateChartSpanUnit: {
			type: String,
			required: false,
			default: "years",
		},
		timeChartSpan: {
			type: Number,
			required: false,
			default: 1,
		},
		timeChartSpanUnit: {
			type: String,
			required: false,
			default: "weeks",
		},
		// トレイを表示するかどうか syncすること
		show_tray: {
			type: Boolean,
			required: false,
			default: false,
		},
		// 日付下のユニットの高さ 指定するとユニットを表示
		topDateUnitHeight: {
			type: Number,
			required: false,
			default: 0,
		},
		// 日毎のガントチャートだけを表示する場合
		onlyDateChartType: {
			type: Boolean,
			required: false,
			default: false,
		},
		taskTitleHeight: {
			type: Number,
			required: false,
			default: 80,
		},
		showCells: {
			type: Boolean,
			required: false,
			default: false,
		},
		darkColorUntil: {
			type: String,
			required: false,
		},
		darkColorFrom: {
			type: String,
			required: false,
		},
		barClickable: {
			type: Boolean,
			required: false,
			default: false,
		},
	},
	data() {
		return {
			start_month: moment().subtract(5, "months").startOf("month").format("YYYY-MM-DD"),
			end_month: moment().add(6, "months").endOf("month").format("YYYY-MM-DD"),
			block_size: 30,
			time_block_size: 48,
			block_number: 0,
			calendars: [],
			inner_width: 0,
			inner_height: 0,
			task_width: 0,
			task_height: 40,
			// task_section_height: 0,
			today: moment(),
			position_id: 0,
			dragging: false,
			chart: "date",
			pageX: "",
			element: "",
			left: "",
			task_id: "",
			taskBar: null,
			taskLine: null,
			width: "",
			leftResizing: false,
			rightResizing: false,
			task: "",
			isValid: false,
			selectedTask: null,
			isResize: false,
			isDragging: false,
			menu: false,
			diff: null,
			isShowDisplayTime: false,
			displayTime: null,
		}
	},
	methods: {
		getWindowSize() {
			this.$nextTick(() => {
				this.inner_width = window.innerWidth
				this.inner_height = window.innerHeight
				if (!this.$vuetify.breakpoint.xs) this.task_width = this.$refs.task.offsetWidth
			})
		},
		todayPosition() {
			this.$nextTick(() => {
				let start_date = moment(this.start_month)
				let between_days = this.today.diff(start_date, "days")
				this.$refs.ganttchart.$refs.calendar.scrollLeft =
					(between_days + 1) * this.block_size - this.calendarViewWidth / 2
			})
		},
		changePeriod(period) {
			if (this.chartType == "time") {
				this.start_month = moment(this.start_month)
					.add(period.number * this.timeChartSpan, this.timeChartSpanUnit)
					.format("YYYY-MM-DD")
				this.end_month = moment(this.end_month)
					.add(period.number * this.timeChartSpan, this.timeChartSpanUnit)
					.format("YYYY-MM-DD")
			} else if (this.chartType == "date") {
				this.start_month = moment(this.start_month)
					.add(period.number * this.dateChartSpan, this.dateChartSpanUnit)
					.format("YYYY-MM-DD")
				this.end_month =
					this.dateChartSpanUnit == "years" || this.dateChartSpanUnit == "months"
						? moment(this.end_month)
								.add(period.number * this.dateChartSpan, this.dateChartSpanUnit)
								.endOf("month")
								.format("YYYY-MM-DD")
						: moment(this.end_month)
								.add(period.number * this.dateChartSpan, this.dateChartSpanUnit)
								.format("YYYY-MM-DD")
			}
			this.$emit("change-display-date", { start_date: this.start_month, end_date: this.end_month })
		},
		changeChartType(date, type) {
			let span
			if (type == "time") {
				if (this.timeChartSpanUnit == "weeks") {
					span = "isoWeek"
				} else if (this.timeChartSpanUnit == "months") {
					span = "month"
				}
				this.start_month = moment(date).startOf(span).format("YYYY-MM-DD")
				this.end_month = moment(date).endOf(span).format("YYYY-MM-DD")
			} else if (type == "date") {
				span = "month"
				let totalMonths = this.dateChartSpan
				if (this.dateChartSpanUnit == "years") {
					totalMonths = this.dateChartSpan * 12
				}
				let half = Math.floor(totalMonths / 2)
				this.start_month = moment(date)
					.subtract(totalMonths % 2 == 1 ? half : half - 1, "months")
					.startOf(span)
					.format("YYYY-MM-DD")
				this.end_month = moment(date).add(half, "months").endOf(span).format("YYYY-MM-DD")
			}
			this.chartType = type
			this.scrollCalendar(date)
			this.$emit("change-display-date", { start_date: this.start_month, end_date: this.end_month })
		},
		setStartDay(date) {
			let span
			if (this.chartType == "time") {
				if (this.timeChartSpanUnit == "weeks") {
					span = "isoWeek"
				} else if (this.timeChartSpanUnit == "months") {
					span = "month"
				}
				this.start_month = moment(date).startOf(span).format("YYYY-MM-DD")
				this.end_month = moment(date).endOf(span).format("YYYY-MM-DD")
			} else if (this.chartType == "date") {
				span = "month"
				let totalMonths = this.dateChartSpan
				if (this.dateChartSpanUnit == "years") {
					totalMonths = this.dateChartSpan * 12
				}
				let half = Math.floor(totalMonths / 2)
				this.start_month = moment(date)
					.subtract(totalMonths % 2 == 1 ? half : half - 1, "months")
					.startOf(span)
					.format("YYYY-MM-DD")
				this.end_month = moment(date).add(half, "months").endOf(span).format("YYYY-MM-DD")
			}
			this.scrollCalendar(date)
			this.$emit("change-display-date", { start_date: this.start_month, end_date: this.end_month })
		},
		dragTask(dragTask) {
			this.task = dragTask
		},
		dragTaskOver(overTask) {
			let deleteIndex
			let addIndex
			if (this.task.cat !== "category") {
				if (overTask.cat === "category") {
					let updateTask = this.tasks.find((task) => task.id === this.task.id)
					updateTask[this.categoryRecognitionId] = overTask["id"]
				} else {
					if (overTask.id !== this.task.id) {
						this.tasks.map((task, index) => {
							if (task.id === this.task.id) deleteIndex = index
						})
						this.tasks.map((task, index) => {
							if (task.id === overTask.id) addIndex = index
						})
						this.tasks.splice(deleteIndex, 1)
						this.task[this.categoryRecognitionId] = overTask[this.categoryRecognitionId]
						this.tasks.splice(addIndex, 0, this.task)
					}
				}
			}
		},
		dragUpdateTask() {
			if (this.isDraggable) {
				let updateTask = this.tasks.find((task) => task.id === this.task.id)
				this.$emit("update-task", {
					id: updateTask.id,
					[this.categoryRecognitionId]: updateTask[this.categoryRecognitionId],
				})
			}
		},
		dragUpdateTaskInTray() {
			if (this.isDraggable) {
				let updateTask = this.tasks.find((task) => task.id === this.task.id)
				this.$emit("remove-task", updateTask.id)
			}
		},
		toggleCategory(task_id) {
			let category = this.categories.find((category) => category.id === task_id)
			const index = this.categories.indexOf(category)
			category["collapsed"] = !category["collapsed"]
			this.categories[index] = category
			this.$set(this.categories, index, category)
		},

		showDialog(id, index) {
			this.$emit("show-task", id, index)
		},
		sendValue(category_value, task_value) {
			this.$emit("distribute-requirement", category_value, task_value)
		},
		sendParent(e) {
			this.$emit("change-datetime", e)
		},
		scrollCalendar(input_date) {
			if (this.chartType == "time") {
				this.$nextTick(() => {
					const date = moment(input_date)
					let start_date = moment(this.start_month)
					let between_hours = date.diff(start_date, "hour")
					this.$refs.ganttchart.$refs.calendar.scrollLeft =
						(between_hours + 1) * this.time_block_size
				})
			} else if (this.chartType == "date") {
				this.$nextTick(() => {
					const date = moment(input_date)
					let start_date = moment(this.start_month)
					let between_days = date.diff(start_date, "days")
					this.$refs.ganttchart.$refs.calendar.scrollLeft =
						(between_days + 1) * this.block_size - this.calendarViewWidth / 2
				})
			}
		},
		clickCategory(id) {
			this.$emit("click-category", id)
		},
		clickTask(id) {
			this.$emit("click-task", id)
		},
		clickStartTime(task) {
			this.$emit("click-start-time", task)
		},
		clickEndTime(task) {
			this.$emit("click-end-time", task)
		},
	},
	computed: {
		taskWithoutCategoryId() {
			return this.tasks.filter((task) => task[this.categoryRecognitionId] === null)
		},
		setDateForChartTypeChange() {
			if (this.displayDate) {
				return this.displayDate
			} else {
				return this.chartType == "date"
					? this.today.format("YYYY-MM-DD")
					: moment(this.start_month).format("YYYY-MM-DD")
			}
		},
		calendarViewWidth() {
			return this.inner_width - this.task_width
		},
		scrollDistance() {
			let start_date = moment(this.start_month)
			let between_days = this.today.diff(start_date, "days")
			return (between_days + 1) * this.block_size - this.calendarViewWidth / 2
		},
		lists() {
			let lists = []
			if (this.categories.length) {
				this.categories.map((category) => {
					if (!this.showTray || category.id != null) {
						lists.push({ cat: "category", ...category })
						this.tasks.map((task) => {
							if (task[this.categoryRecognitionId] === category.id && !category.collapsed) {
								lists.push({ cat: "task", ...task })
							}
						})
					}
				})
			} else {
				this.tasks.map((task) => {
					lists.push({ cat: "task", ...task })
				})
			}
			return lists
		},
		displayDate: {
			get() {
				return this.display_date
			},
			set(value) {
				this.$emit("update:display_date", value)
			},
		},
		showTray: {
			get() {
				return this.show_tray
			},
			set(value) {
				this.$emit("update:show_tray", value)
			},
		},
		chartType: {
			get() {
				return this.syncChartType ? this.chart_type : this.chart
			},
			set(value) {
				return this.syncChartType ? this.$emit("update:chart_type", value) : (this.chart = value)
			},
		},
	},
	mounted() {
		if (this.start_date) {
			this.setStartDay(this.start_date)
		}
		window.addEventListener("resize", this.getWindowSize)
		this.$nextTick(() => {
			this.getWindowSize()
			this.todayPosition()
		})
	},
	watch: {
		display_date(newVal, _) {
			this.setStartDay(newVal)
		},
		start_date(newVal, _) {
			this.start_month = newVal
		},
		end_date(newVal, _) {
			this.end_month = newVal
		},
		tasks(newVal, _) {
			if (!this.selectedTask) return
			const task = newVal.find((t) => t.id == this.selectedTask.id)
			this.selectedTask = task
		},
		deep: true,
	},
}
</script>

<style scoped lang="scss">
@import "../../stylesheet/shared/color.scss";
.attention {
	font-weight: bold;
}
.gantt {
	user-select: none;
}
.gantt-chart {
	position: relative;
	display: flex;
	.gantt-tasks {
		height: 5rem;
		display: flex;
		background: $sub-color;
		.gantt-task {
			border-right: solid 1px white;
			font-weight: bold;
			color: white;
			display: flex;
			align-items: center;
			justify-content: center;
			// width: 12rem;
		}
		.gantt-start,
		.gantt-end {
			border-right: solid 1px white;
			// border-top: solid 1px white;
			// border-bottom: solid 1px white;
			font-weight: bold;
			color: white;
			display: flex;
			align-items: center;
			justify-content: center;
			// width: 6rem;
		}
	}
}
.gantt-task-list {
	display: flex;
	height: 40px;
	width: 384px;
	border-bottom: solid 1px #80808045;
}

.categories {
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: rgb(255 228 0 / 20%);
	font-weight: bold;
	padding-left: 10px;
	width: 100%;
	border-right: solid 1px #80808045;
	cursor: pointer;
	span {
		font-size: var(--fontSize);
	}
}

.tasks {
	display: flex;
	border-left: solid 1px #80808045;
}

.task-name {
	padding: 4px;
	display: flex;
	border-right: solid 1px #80808045;
	align-items: center;
	padding-left: 16px;
	// width: 12rem;
	font-size: 15px;
	justify-content: center;
}
.task-start,
.task-end {
	display: flex;
	border-right: solid 1px #80808045;
	align-items: center;
	// width: 6rem;
	font-size: 15px;
	justify-content: center;
}
.gantt-year-month {
	position: relative;
	height: 2rem;
}
.gantt-date {
	height: 5rem;
}

.calendar-date {
	position: absolute;
	background: $main-color;
	color: white;
	font-weight: bold;
	border-bottom: solid 1px #80808045;
	border-right: solid 1px #80808045;
	border-top: solid 1px #80808045;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 2rem;
}
.gantt-day {
	position: relative;
	height: 3rem;
}
.gantt-days {
	position: absolute;
	border-bottom: solid 1px #80808045;
	border-right: solid 1px #80808045;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: bold;
	height: 3rem;
	font-size: 0.8rem;
	flex-direction: column;
}
.saturday {
	background: #dbeafe;
}
.sunday {
	background: #fee2e2;
}
.today {
	background: #dc2626;
	color: white;
}
.gantt-height {
	position: relative;
}
.days-height {
	position: absolute;
	border-bottom: solid 1px #80808045;
	border-right: solid 1px #80808045;
}
.gantt-bar-area {
	position: relative;
}

.gantt-bar {
	position: absolute;
	background: orange;
	height: 20px;
	border-radius: 10px;
	&--true {
		cursor: grab;
	}
	&__name {
		overflow: hidden;
		width: 100%;
	}
	&__left {
		top: 6px;
		left: -6px;
		cursor: col-resize;
		position: absolute;
		background: #808080a1;
		height: 8px;
		width: 8px;
	}

	&__right {
		top: 6px;
		right: -6px;
		cursor: col-resize;
		position: absolute;
		background: #808080a1;
		height: 8px;
		width: 8px;
	}
	.display-time-left {
		background: whitesmoke;
		position: absolute;
		padding: 4px;
		font-size: 0.9rem;
		border-radius: 5px;
		bottom: 27px;
		text-align: center;
		opacity: 0.8;
	}

	.display-time-right {
		background: whitesmoke;
		position: absolute;
		padding: 4px;
		font-size: 0.9rem;
		border-radius: 5px;
		bottom: 27px;
		text-align: center;
		opacity: 0.8;
		right: 6px;
	}
}

.gantt-line {
	position: absolute;
	height: 20px;
	border-bottom: 2px solid;
	border-color: orange;
	&__name {
		height: 100%;
		width: 100%;
		overflow: hidden;
		width: 100%;
		&:hover {
			background-color: rgba(255, 255, 255, 0.5);
		}
	}
	&__left {
		bottom: -4px;
		left: -4px;
		cursor: col-resize;
		position: absolute;
		background: orange;
		height: 8px;
		width: 8px;
		border-radius: 50%;
	}

	&__right {
		bottom: -4px;
		right: -4px;
		cursor: col-resize;
		position: absolute;
		background: orange;
		height: 8px;
		width: 8px;
		border-radius: 50%;
	}
	.display-time-left {
		background: whitesmoke;
		position: absolute;
		padding: 4px;
		font-size: 0.9rem;
		border-radius: 5px;
		bottom: 27px;
		text-align: center;
		opacity: 0.8;
	}

	.display-time-right {
		background: whitesmoke;
		position: absolute;
		padding: 4px;
		font-size: 0.9rem;
		border-radius: 5px;
		bottom: 27px;
		text-align: center;
		opacity: 0.8;
		right: 6px;
	}
}
.bottom-sheet {
	z-index: 100;
	height: 100px;
	width: 100vw;
	position: fixed;
	left: 0;
	bottom: 0;
	overflow: scroll;
}
.modified-height {
	z-index: 0;
	height: 100px;
	position: relative;
}
.fab-btn {
	z-index: 101;
	bottom: 80px;
	right: 24px;
}

.grab {
	cursor: grab;
}
.gantt-bar-task {
	position: absolute;
	background: orange;
	border-radius: 10px;
}
.hoge {
	z-index: 2;
	position: absolute;
	right: 0;
}
</style>
